import { useEffect, useState } from "react";
import { useThree } from "@react-three/fiber";
import { useModelCompileTick } from "@/stores/modelCompileStore";

// Compiles an object's shader programs on the driver's worker threads
// (KHR_parallel_shader_compile) before the object is first drawn. Returns
// true once every program reports ready via the non-blocking
// COMPLETION_STATUS query, or after a 3s safety deadline.
//
// Drawing a model before its programs are ready forces a synchronous
// compile on the main thread (up to 100ms+ per program on Windows/ANGLE),
// which stutters the whole visible scene. Callers should keep the model
// hidden (and its loading placeholder up) until this returns true.
export default function useCompileReady(object) {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!object) return undefined;
    let cancelled = false;
    let timer;
    let materials;
    try {
      // compile(object, camera, targetScene) links the object's programs
      // against the main scene's lighting without waiting on them.
      materials = [...gl.compile(object, camera, scene)];
    } catch {
      setReady(true);
      return undefined;
    }
    const deadline = performance.now() + 3000;
    const poll = () => {
      if (cancelled) return;
      materials = materials.filter((m) => {
        const entry = gl.properties.get(m);
        return entry && entry.currentProgram && !entry.currentProgram.isReady();
      });
      if (materials.length === 0 || performance.now() > deadline) {
        setReady(true);
        // Signal that another model is fully in the scene; the shadow
        // re-bake debounces on this.
        useModelCompileTick.getState().ping();
      } else {
        timer = setTimeout(poll, 10);
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [object, gl, scene, camera]);

  return ready;
}
